import { useState, useEffect, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { X, Search, Trash2, Package } from 'lucide-react';
import { useDepartments } from '@/hooks/use-departments';
import { useBarcodeScan } from '@/hooks/use-barcode-scan';
import { api } from '@/lib/api-client';
import { formatNumber } from '@/lib/utils';
import { MultiSelect } from '@/components/ui/multi-select';
import { prefillLineCost } from '@/lib/private-warehouse';

// Cost / total columns are deliberately stripped from the entire PO + SO
// (warehouse) picker flow — buyers + warehouse staff only need quantity and
// unit while drafting. The role-based useCanSeeCost gate is intentionally
// bypassed; flip it back on by reintroducing the columns AND the hook if a
// future requirement asks for it.

const VENDOR_PRODUCTS_PAGE_SIZE = 500;

interface SelectedProduct {
  id: string;
  name: string;
  primaryBarcode: string;
  departmentName: string;
  cost: string;
  sellingPrice: string;
  inStockQty: string;
  reorderThreshold: string | null;
  reorderQuantity: string | null;
  defaultSellingUnit: string;
  defaultPurchasingUnit?: string;
  soldByWeight: boolean;
  quantity: string;
  costPerUnit: string;
  // The product's productUnits[] rows so the parent form can resolve baseQty
  // for the chosen purchasing unit (e.g. "case" → 10 each). Without this the
  // form's findBaseQty falls back to 1 even when the unit IS configured on
  // the product, which is what kept the "= N each" preview at zero.
  units?: Array<{ unitName: string; baseQty: number | string }>;
}

interface ProductPickerModalProps {
  payeeId: string;
  locationId: string;
  existingProductIds: Set<string>;
  onAdd: (products: SelectedProduct[]) => void;
  onClose: () => void;
}

type Tab = 'products' | 'selected';

export function ProductPickerModal({
  payeeId,
  locationId,
  existingProductIds,
  onAdd,
  onClose,
}: ProductPickerModalProps) {
  const [tab, setTab] = useState<Tab>('products');
  const [search, setSearch] = useState('');
  const scan = useBarcodeScan((code) => setSearch(code));
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Multi-select department filter — empty set means "all departments". The
  // earlier single-string filter forced users to repeatedly switch the dropdown
  // when they wanted PRODUCE + SWEETS in one PO.
  const [departmentIds, setDepartmentIds] = useState<Set<string>>(new Set());
  const [lowThreshold, setLowThreshold] = useState(false);
  const [needsReorder, setNeedsReorder] = useState(false);
  const [selected, setSelected] = useState<Map<string, SelectedProduct>>(new Map());

  // The picker is ALWAYS scoped to the selected vendor — a PO is being placed
  // with that vendor, so non-vendor products are never relevant. The earlier
  // "Vendor's Products" toggle let the user expand to the full catalog, which
  // bled non-vendor rows into the low-threshold view (the bug QA reported).
  // Toggle removed; vendor scope is implicit.
  const hasVendor = !!payeeId;

  const { data: deptData } = useDepartments();
  const departments = deptData?.data ?? [];

  // Debounce search → client-side filter over the loaded vendor catalog.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 200);
    return () => clearTimeout(t);
  }, [search]);

  // Pages through /payees/:id/products and accumulates every page so search /
  // department / low-threshold refinements operate on the full vendor catalog
  // without a second network round-trip per filter change.
  const vendorProductsQuery = useInfiniteQuery({
    queryKey: ['payee-products', payeeId, locationId],
    queryFn: ({ pageParam = 1 }) =>
      api.get<{ data: any[]; pagination: { page: number; perPage: number; total: number; totalPages: number } }>(
        `/payees/${payeeId}/products`,
        {
          ...(locationId ? { locationId } : {}),
          page: pageParam,
          perPage: VENDOR_PRODUCTS_PAGE_SIZE,
        },
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage?.pagination) return undefined;
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    enabled: hasVendor,
  });

  useEffect(() => {
    if (vendorProductsQuery.hasNextPage && !vendorProductsQuery.isFetchingNextPage) {
      vendorProductsQuery.fetchNextPage();
    }
  }, [
    vendorProductsQuery.hasNextPage,
    vendorProductsQuery.isFetchingNextPage,
    vendorProductsQuery.data,
    vendorProductsQuery,
  ]);

  // Apply department / search / low-threshold refinements on the loaded
  // vendor catalog. The list is bounded by the vendor's own catalog, so
  // client-side filtering is fine. Result is sorted by department then by
  // product name so the picker reads as grouped sections — buyers asked to
  // see PRODUCE rows together, alphabetised, instead of insertion order.
  const vendorList = useMemo(() => {
    let rows: any[] = vendorProductsQuery.data?.pages.flatMap((p) => p.data) ?? [];
    if (departmentIds.size > 0) {
      rows = rows.filter((p: any) => p.departmentId && departmentIds.has(p.departmentId));
    }
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      rows = rows.filter(
        (p: any) =>
          p.name.toLowerCase().includes(q) ||
          (p.primaryBarcode && p.primaryBarcode.includes(q)) ||
          (p.sku && p.sku.toLowerCase().includes(q)),
      );
    }
    if (lowThreshold) {
      // "Low" means: stock is at-or-below the configured reorder threshold OR
      // the product is out of stock at this location. The previous filter
      // hard-required a threshold to be set, which silently dropped products
      // that genuinely needed reorder but happened to lack a threshold value
      // — that was the "filter not working for some products" report. The
      // threshold itself comes from /payees/:id/products which already
      // resolves the per-location override (falling back to the product-wide
      // value), so the only knob the client has to be careful about is the
      // null-threshold case below.
      rows = rows.filter((p: any) => {
        const stock = Number(p.inStockQty) || 0;
        const raw = p.reorderThreshold;
        const hasThreshold = raw !== null && raw !== undefined && raw !== '';
        if (!hasThreshold) return stock === 0;
        const t = Number(raw);
        if (Number.isNaN(t)) return stock === 0;
        return stock <= t;
      });
    }
    if (needsReorder) {
      rows = rows.filter((p: any) => {
        // Force-include products are always part of the auto PO for this
        // location, so the "what needs reordering" view must show them even
        // without a threshold/reorder qty — mirrors auto-po-service.
        if (p.forceIncludeAutoPo) return true;
        const stock = Number(p.inStockQty) || 0;
        const raw = p.reorderThreshold;
        const hasThreshold = raw !== null && raw !== undefined && raw !== '';
        const reorderQty = Number(p.reorderQuantity) || 0;
        if (!reorderQty || !hasThreshold) return false;
        const t = Number(raw);
        if (Number.isNaN(t)) return false;
        if (stock >= t) return false;
        // For warehouse vendors: warehouse must have enough to fulfill reorder qty
        const whStock = p.warehouseStock !== null && p.warehouseStock !== undefined ? Number(p.warehouseStock) : null;
        if (whStock !== null && whStock < reorderQty) return false;
        return true;
      });
    }
    // Stable sort: department asc (empty department goes last), then name asc.
    const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
    return [...rows].sort((a, b) => {
      const aDept = (a.departmentName || '').trim();
      const bDept = (b.departmentName || '').trim();
      if (aDept && !bDept) return -1;
      if (!aDept && bDept) return 1;
      const deptCmp = collator.compare(aDept, bDept);
      if (deptCmp !== 0) return deptCmp;
      return collator.compare(a.name || '', b.name || '');
    });
  }, [vendorProductsQuery.data, departmentIds, debouncedSearch, lowThreshold, needsReorder]);

  // Paginate the refined list client-side so the table stays scrollable.
  // Reset to page 1 whenever the filter inputs change (React's recommended
  // reset-on-render pattern — avoids a wasted second render from useEffect).
  const [vendorPage, setVendorPage] = useState(1);
  const vendorFilterKey = `${[...departmentIds].sort().join(',')}|${lowThreshold ? '1' : '0'}|${needsReorder ? '1' : '0'}|${debouncedSearch}`;
  const [prevVendorFilterKey, setPrevVendorFilterKey] = useState(vendorFilterKey);
  if (vendorFilterKey !== prevVendorFilterKey) {
    setPrevVendorFilterKey(vendorFilterKey);
    setVendorPage(1);
  }
  const VENDOR_VIEW_PAGE_SIZE = 50;
  const vendorTotal = vendorList.length;
  const vendorTotalPages = Math.max(1, Math.ceil(vendorTotal / VENDOR_VIEW_PAGE_SIZE));
  const vendorPageItems = useMemo(
    () => vendorList.slice((vendorPage - 1) * VENDOR_VIEW_PAGE_SIZE, vendorPage * VENDOR_VIEW_PAGE_SIZE),
    [vendorList, vendorPage],
  );

  const isLoading = vendorProductsQuery.isLoading;
  const isLoadingMore = vendorProductsQuery.isFetchingNextPage;
  const totalVendorProducts = vendorProductsQuery.data?.pages[0]?.pagination?.total ?? null;
  const loadedVendorProducts = vendorProductsQuery.data?.pages.reduce((sum, p) => sum + p.data.length, 0) ?? 0;

  const filteredProducts: any[] = hasVendor ? vendorPageItems : [];
  const currentPage = vendorPage;
  const setCurrentPage = setVendorPage;
  const totalPages = vendorTotalPages;
  const totalItems = vendorTotal;

  const toggleProduct = (product: any) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(product.id)) {
        next.delete(product.id);
      } else {
        next.set(product.id, {
          id: product.id,
          name: product.name,
          primaryBarcode: product.primaryBarcode || '',
          departmentName: product.departmentName || '',
          cost: product.cost || '0',
          sellingPrice: product.sellingPrice || '',
          inStockQty: product.inStockQty || '0',
          reorderThreshold: product.reorderThreshold,
          reorderQuantity: product.reorderQuantity ?? null,
          defaultSellingUnit: product.defaultSellingUnit || 'each',
          defaultPurchasingUnit: product.defaultPurchasingUnit || 'each',
          soldByWeight: !!product.soldByWeight,
          quantity: product.reorderQuantity ? String(product.reorderQuantity) : '1',
          costPerUnit: prefillLineCost(product, product.defaultPurchasingUnit || 'each'),
          units: product.units ?? [],
        });
      }
      return next;
    });
  };

  const toggleAll = () => {
    const allVisible = filteredProducts.filter((p: any) => !existingProductIds.has(p.id));
    const allSelected = allVisible.every((p: any) => selected.has(p.id));

    setSelected((prev) => {
      const next = new Map(prev);
      if (allSelected) {
        allVisible.forEach((p: any) => next.delete(p.id));
      } else {
        allVisible.forEach((p: any) => {
          if (!next.has(p.id)) {
            next.set(p.id, {
              id: p.id,
              name: p.name,
              primaryBarcode: p.primaryBarcode || '',
              departmentName: p.departmentName || '',
              cost: p.cost || '0',
              sellingPrice: p.sellingPrice || '',
              inStockQty: p.inStockQty || '0',
              reorderThreshold: p.reorderThreshold,
              reorderQuantity: p.reorderQuantity ?? null,
              defaultSellingUnit: p.defaultSellingUnit || 'each',
              defaultPurchasingUnit: p.defaultPurchasingUnit || 'each',
              soldByWeight: !!p.soldByWeight,
              quantity: p.reorderQuantity ? String(p.reorderQuantity) : '1',
              costPerUnit: prefillLineCost(p, p.defaultPurchasingUnit || 'each'),
              units: p.units ?? [],
            });
          }
        });
      }
      return next;
    });
  };

  const updateSelected = (id: string, field: 'quantity' | 'costPerUnit', value: string) => {
    setSelected((prev) => {
      const next = new Map(prev);
      const item = next.get(id);
      if (item) {
        next.set(id, { ...item, [field]: value });
      }
      return next;
    });
  };

  const removeSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };

  const clearFilters = () => {
    setSearch('');
    setDepartmentIds(new Set());
    setLowThreshold(false);
    setNeedsReorder(false);
  };

  // "Active filter" only reflects refinements now — vendor scope is implicit.
  const hasActiveFilters = !!search || departmentIds.size > 0 || lowThreshold || needsReorder;

  const handleAdd = () => {
    onAdd(Array.from(selected.values()));
    onClose();
  };

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const selectableProducts = filteredProducts.filter((p: any) => !existingProductIds.has(p.id));
  const allSelectableChecked =
    selectableProducts.length > 0 && selectableProducts.every((p: any) => selected.has(p.id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-5xl flex-col rounded-xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold">Select Products for Purchase Order</h2>
          <button
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border px-6">
          <button
            onClick={() => setTab('products')}
            className={`relative px-4 py-3 text-sm font-medium transition-colors ${
              tab === 'products' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Product List
            {tab === 'products' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
          </button>
          <button
            onClick={() => setTab('selected')}
            className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
              tab === 'selected' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Selected Products
            {selected.size > 0 && (
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold text-white">
                {selected.size}
              </span>
            )}
            {tab === 'selected' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {tab === 'products' ? (
            <div className="p-6">
              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={scan.onKeyDown}
                  onBlur={scan.onBlur}
                  className="h-9 w-full max-w-sm rounded-lg border border-input bg-background pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>

              {/* Filter toggles row — Low Threshold and Department refine the
                  vendor's catalog. Vendor scope itself is implicit (the picker
                  only ever shows products belonging to the selected vendor). */}
              <div className="mb-4 flex flex-wrap items-center gap-4">
                {/* Low Threshold Products toggle */}
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <span className="text-sm text-foreground">Low Threshold Products</span>
                  <button
                    type="button"
                    onClick={() => setLowThreshold(!lowThreshold)}
                    className={`relative h-6 w-11 rounded-full transition-colors ${lowThreshold ? 'bg-primary' : 'bg-gray-200'}`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${lowThreshold ? 'translate-x-5' : ''}`}
                    />
                  </button>
                </label>

                {/* Needs Reorder toggle — shows products below threshold with reorder qty set */}
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <span className="text-sm text-foreground">Needs Reorder</span>
                  <button
                    type="button"
                    onClick={() => setNeedsReorder(!needsReorder)}
                    className={`relative h-6 w-11 rounded-full transition-colors ${needsReorder ? 'bg-primary' : 'bg-gray-200'}`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${needsReorder ? 'translate-x-5' : ''}`}
                    />
                  </button>
                </label>

                {/* Departments multi-select — buyers asked for this so a single
                    PO can mix PRODUCE + SWEETS (etc.) without flicking the
                    dropdown back and forth between filter passes. */}
                <MultiSelect
                  className="w-64"
                  triggerLabel="Departments"
                  options={departments.map((d: any) => ({ value: d.id, label: d.name }))}
                  selected={[...departmentIds]}
                  onChange={(values) => setDepartmentIds(new Set(values))}
                  searchPlaceholder="Search departments..."
                  emptyMessage="No departments"
                />

                {hasActiveFilters && (
                  <button onClick={clearFilters} className="text-sm font-medium text-primary hover:underline">
                    Clear filters
                  </button>
                )}
              </div>

              {/* Product Table */}
              {!hasVendor ? (
                <div className="flex h-48 flex-col items-center justify-center text-muted-foreground">
                  <Package className="mb-2 h-8 w-8 text-muted-foreground/50" />
                  <p className="text-sm">Select a vendor to load products</p>
                </div>
              ) : isLoading ? (
                <div className="flex h-48 items-center justify-center text-muted-foreground">Loading products...</div>
              ) : filteredProducts.length === 0 ? (
                <div className="flex h-48 flex-col items-center justify-center text-muted-foreground">
                  <Package className="mb-2 h-8 w-8 text-muted-foreground/50" />
                  <p className="text-sm">No products found</p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="w-10 px-3 py-2.5">
                          <input
                            type="checkbox"
                            checked={allSelectableChecked}
                            onChange={toggleAll}
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                          />
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">Product</th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">Department</th>
                        <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground">
                          Store Stock
                        </th>
                        <th
                          className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground"
                          title="Stock at the vendor's linked warehouse — only shown for warehouse vendors"
                        >
                          Warehouse Stock
                        </th>
                        <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground">
                          Reorder Threshold
                        </th>
                        <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground">
                          Reorder Qty
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((product: any) => {
                        const isExisting = existingProductIds.has(product.id);
                        const isChecked = selected.has(product.id);
                        const storeStock = Number(product.storeStock ?? product.inStockQty) || 0;
                        const warehouseStock =
                          product.warehouseStock === null || product.warehouseStock === undefined
                            ? null
                            : Number(product.warehouseStock);
                        const threshold = product.reorderThreshold ? Number(product.reorderThreshold) : null;
                        const reorderQty = product.reorderQuantity ? Number(product.reorderQuantity) : null;
                        const isBelowThreshold = threshold !== null && storeStock <= threshold;
                        const insufficientWarehouse =
                          reorderQty !== null && warehouseStock !== null && warehouseStock < reorderQty;

                        return (
                          <tr
                            key={product.id}
                            onClick={() => !isExisting && toggleProduct(product)}
                            className={`border-b border-border last:border-0 cursor-pointer transition-colors ${
                              isExisting
                                ? 'opacity-40 cursor-not-allowed bg-muted/30'
                                : isChecked
                                  ? 'bg-primary/5'
                                  : 'hover:bg-muted/30'
                            }`}
                          >
                            <td className="px-3 py-2.5">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={isExisting}
                                onChange={() => !isExisting && toggleProduct(product)}
                                onClick={(e) => e.stopPropagation()}
                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
                              />
                            </td>
                            <td className="px-3 py-2.5">
                              <div className="flex items-center gap-2">
                                <div>
                                  <div className="text-sm font-medium">{product.name}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {product.primaryBarcode || product.sku || '—'}
                                    {product.forceIncludeAutoPo && (
                                      <span
                                        className="ml-2 inline-flex rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
                                        title="Always included in auto purchase orders for this location"
                                      >
                                        Force include
                                      </span>
                                    )}
                                    {isExisting && (
                                      <span className="ml-2 inline-flex rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                                        Already added
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-2.5 text-sm text-muted-foreground">
                              {product.departmentName || '—'}
                            </td>
                            <td className="px-3 py-2.5 text-right">
                              <span className={`text-sm ${isBelowThreshold ? 'font-semibold text-red-600' : ''}`}>
                                {formatNumber(storeStock)}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-right text-sm text-muted-foreground">
                              {warehouseStock !== null
                                ? `${formatNumber(warehouseStock)}${product.warehouseSellingUnit ? ` ${product.warehouseSellingUnit}` : ''}`
                                : '—'}
                            </td>
                            <td className="px-3 py-2.5 text-right text-sm text-muted-foreground">
                              {threshold !== null ? formatNumber(threshold) : '—'}
                            </td>
                            <td className="px-3 py-2.5 text-right">
                              {reorderQty !== null ? (
                                <span
                                  className={`text-sm ${insufficientWarehouse ? 'font-semibold text-red-600' : ''}`}
                                >
                                  {formatNumber(reorderQty)}
                                  {insufficientWarehouse && (
                                    <span
                                      className="ml-1 text-[10px] text-red-500"
                                      title="Warehouse stock insufficient for reorder quantity"
                                    >
                                      !!
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-sm text-muted-foreground">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {isLoadingMore && totalVendorProducts !== null && (
                    <div className="flex items-center justify-center gap-2 border-t border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                      <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground" />
                      Loading more products… ({loadedVendorProducts} of {totalVendorProducts})
                    </div>
                  )}
                  {/* Pagination strip — page through long lists rather than scrolling
                      a single huge table. Vendor mode paginates over the in-memory
                      catalog; catalog mode hits the products service per page. */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-border bg-muted/20 px-3 py-2 text-xs">
                      <span className="text-muted-foreground">
                        Page {currentPage} of {totalPages} · {totalItems} item{totalItems === 1 ? '' : 's'}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage <= 1}
                          className="inline-flex h-7 items-center rounded-md border border-input bg-white px-2 font-medium text-foreground hover:bg-muted disabled:opacity-40"
                        >
                          Prev
                        </button>
                        <button
                          type="button"
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage >= totalPages}
                          className="inline-flex h-7 items-center rounded-md border border-input bg-white px-2 font-medium text-foreground hover:bg-muted disabled:opacity-40"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* Selected Products Tab */
            <div className="p-6">
              {selected.size === 0 ? (
                <div className="flex h-48 flex-col items-center justify-center text-muted-foreground">
                  <Package className="mb-2 h-8 w-8 text-muted-foreground/50" />
                  <p className="text-sm">No products selected</p>
                  <button
                    onClick={() => setTab('products')}
                    className="mt-2 text-sm font-medium text-primary hover:underline"
                  >
                    Browse products
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">Product</th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">Department</th>
                        <th className="w-28 px-3 py-2.5 text-right text-xs font-medium text-muted-foreground">Qty</th>
                        <th className="w-10 px-3 py-2.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from(selected.values()).map((item) => {
                        return (
                          <tr key={item.id} className="border-b border-border last:border-0">
                            <td className="px-3 py-2.5">
                              <div className="text-sm font-medium">{item.name}</div>
                              <div className="text-xs text-muted-foreground">{item.primaryBarcode || '—'}</div>
                            </td>
                            <td className="px-3 py-2.5 text-sm text-muted-foreground">{item.departmentName || '—'}</td>
                            <td className="px-3 py-2.5">
                              <input
                                type="number"
                                step={item.soldByWeight ? '0.001' : '1'}
                                min={item.soldByWeight ? '0.001' : '1'}
                                value={item.quantity}
                                onChange={(e) => updateSelected(item.id, 'quantity', e.target.value)}
                                className="h-8 w-full rounded border border-input px-2 text-right text-sm outline-none focus:border-primary"
                              />
                            </td>
                            <td className="px-3 py-2.5">
                              <button
                                type="button"
                                onClick={() => removeSelected(item.id)}
                                className="inline-flex h-7 w-7 items-center justify-center rounded border border-border text-red-500 hover:bg-red-50"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <div className="text-sm">
            {selected.size === 0 && tab === 'products' ? (
              <span className="text-red-500 font-medium">Please select at least one product before continuing.</span>
            ) : (
              <span className="text-muted-foreground">
                {selected.size} product{selected.size !== 1 ? 's' : ''} selected
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 items-center rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
            >
              Cancel
            </button>
            {tab === 'products' ? (
              <button
                type="button"
                onClick={() => setTab('selected')}
                disabled={selected.size === 0}
                className="inline-flex h-9 items-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={handleAdd}
                disabled={selected.size === 0}
                className="inline-flex h-9 items-center rounded-lg bg-green-600 px-4 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                Save
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
